<script setup lang="ts">
import { type AddressFormFieldData, addressFormFieldSchema } from '@lib/shared';

import { BackendErrorCode, type CreateAddressBodyDto, SellingMethod, useApiComputeLogisticServicePrice } from '~/apiClient';
import type { SaleItemEntity } from '~/types/sale.type';

// PROPS
const { sale } = defineProps({
    sale: { type: Object as PropType<SaleItemEntity>, required: true },
    name: { type: String, default: undefined },
});

// EVENTS
const emit = defineEmits<{
    computePrice: [number | undefined];
}>();

// MODEL
const model = defineModel<AddressFormFieldData | undefined>({ required: true });

// COMPOSABLES
const { mutateAsync: mutateComputePrice, isPending: isPendingCompute } = useApiComputeLogisticServicePrice();
const { tn, tt } = useTypedI18n();
const { logInfo } = useLogs();

// DATA
const errorMessage = ref<string | undefined>();
const address = ref<AddressFormFieldData | undefined>(model.value);
const mutateSaleParam = computed(() => {
    switch (sale.sellingMethod) {
        case SellingMethod.Auction:
            return { auctionId: sale.id };
        case SellingMethod.DirectSale:
            return { directSaleId: sale.id };
    }
});
const price = ref<number | undefined>(undefined);

// HANDLERS
const onUpdatePrice = (value: number | undefined): void => {
    price.value = value;
    emit('computePrice', value);
};

const isValidAddress = (addressFormData: Partial<AddressFormFieldData> | undefined): addressFormData is AddressFormFieldData => {
    errorMessage.value = undefined;
    const validation = addressFormFieldSchema.safeParse(addressFormData);

    if (validation.success) return true;

    const errorSubfields = validation.error.issues.map(issue => issue.path.at(-1));
    logInfo(`Error validating logistic service destination address. Missing or invalid required address fields: ${errorSubfields.join(', ')}`, {
        sellingMethod: sale.sellingMethod,
        saleId: sale.id,
        addressFormData,
    });
    errorMessage.value = tt('vas.LogisticsService.validation.invalidDestination');

    return false;
};

const onCalculate = async (destinationAddress: CreateAddressBodyDto): Promise<void> => {
    await mutateComputePrice({ data: { ...mutateSaleParam.value, destinationAddress } })
        .then(response => {
            onUpdatePrice(response.price);
            model.value = address.value;
        })
        .catch(error => {
            errorMessage.value =
                isBackendError(error) && error.error.code === BackendErrorCode.LogisticsServiceDestinationCountryNotSupported
                    ? tt('vas.LogisticsService.validation.apiError')
                    : tt('vas.LogisticsService.validation.invalidDestination');
        });
};

const onEdit = (): void => {
    onUpdatePrice(undefined);
    address.value = undefined;
    model.value = undefined;
};

const onSubmit = async (): Promise<void> => {
    if (!isValidAddress(address.value)) return;

    if (price.value) return onEdit();

    await onCalculate(address.value);
};

// WATCHERS
watch(address, (value, oldValue) => {
    if (!value) {
        errorMessage.value = undefined;
    } else if (value !== oldValue) {
        onSubmit();
    }
});

// LYFECYCLE
onMounted(() => {
    address.value && onSubmit();
});
</script>

<template>
    <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-4 md:flex-row md:items-start">
            <UFormField :name="name" :label="tt('vas.LogisticsService.destination')" class="grow" :error="errorMessage" required>
                <BaseAddressSearch v-model="address" :disabled="isPendingCompute || !!price" />
                <template #error="{ error }">
                    <span v-if="error" class="text-red inline-flex items-center gap-3 text-base leading-6">
                        <UIcon name="lucide:triangle-alert" class="size-6" /> {{ error }}
                    </span>
                </template>
            </UFormField>
            <UButton
                color="primary"
                variant="outline"
                class="block md:mt-6 md:flex"
                :label="price ? tt('vas.LogisticsService.edit') : tt('vas.LogisticsService.calculate')"
                size="xl"
                :loading="isPendingCompute"
                @click="onSubmit"
            />
        </div>

        <div v-if="price" class="flex justify-between">
            <span class="text-toned font-semibold">{{ tt('vas.LogisticsService.calculatedExpenses') }}</span>
            <span class="text-2xl font-bold"> {{ price && tn(price, 'currency') }} </span>
        </div>
    </div>
</template>
